import React from 'react';
import { observer } from 'mobx-react-lite';
import { Card, Tag, Icon, Divider, H3, H4, H5, Colors } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import changelogData from '../../data/changelog.json';
import { ChangelogData, ChangelogRelease, ChangelogFeature } from '../../types/changelog';
import './ChangelogPage.css';

/**
 * Changelog Page Component
 * Displays version history and release notes for the application
 */
const ChangelogPage: React.FC = observer(() => {
  const data = changelogData as ChangelogData;

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'stable':
      case 'completed':
        return Colors.GREEN3;
      case 'beta':
        return Colors.ORANGE3;
      case 'alpha':
      case 'in-progress':
        return Colors.RED3;
      default:
        return Colors.GRAY3;
    }
  };

  const getImpactColor = (impact: string): string => {
    switch (impact) {
      case 'critical':
        return Colors.RED3;
      case 'major':
        return Colors.ORANGE3;
      case 'minor':
        return Colors.BLUE3;
      default:
        return Colors.GRAY3;
    }
  };

  return (
    <div className="changelog-page">
      {/* Header Section */}
      <div className="changelog-header">
        <div className="header-content">
          <div className="header-title-row">
            <div className="title-icon">
              <Icon icon={IconNames.TIMELINE_EVENTS} size={32} />
            </div>
            <div className="title-content">
              <h1>CHANGELOG</h1>
              <p className="subtitle">Complete development history and release documentation</p>
            </div>
          </div>
          
          <div className="metadata-badges">
            <Tag large intent="primary">
              <Icon icon={IconNames.TAG} size={14} style={{ marginRight: '6px' }} />
              v{data.metadata.currentVersion}
            </Tag>
            <Tag large minimal>
              <Icon icon={IconNames.HISTORY} size={14} style={{ marginRight: '6px' }} />
              {data.metadata.totalReleases} Releases
            </Tag>
            <Tag large minimal>
              <Icon icon={IconNames.CALENDAR} size={14} style={{ marginRight: '6px' }} />
              {data.metadata.releaseSchedule}
            </Tag>
          </div>
        </div>
      </div>

      {/* Metadata Overview Card */}
      <Card className="metadata-card" elevation={2}>
        <div className="metadata-grid">
          <div className="metadata-item">
            <div className="metadata-label">
              <Icon icon={IconNames.BADGE} size={16} />
              CURRENT VERSION
            </div>
            <div className="metadata-value">{data.metadata.currentVersion}</div>
          </div>
          
          <div className="metadata-item">
            <div className="metadata-label">
              <Icon icon={IconNames.UPDATED} size={16} />
              NEXT VERSION
            </div>
            <div className="metadata-value">{data.metadata.nextVersion}</div>
          </div>
          
          <div className="metadata-item">
            <div className="metadata-label">
              <Icon icon={IconNames.TIME} size={16} />
              RELEASE SCHEDULE
            </div>
            <div className="metadata-value">{data.metadata.releaseSchedule}</div>
          </div>
          
          <div className="metadata-item">
            <div className="metadata-label">
              <Icon icon={IconNames.ENDORSED} size={16} />
              SUPPORTED VERSIONS
            </div>
            <div className="metadata-value">
              {data.metadata.supportedVersions.map((version) => (
                <Tag key={version} minimal small style={{ marginRight: '4px' }}>
                  {version}
                </Tag>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Release History */}
      <div className="releases-container">
        {data.releases.map((release: ChangelogRelease) => (
          <Card key={release.version} className="release-card" elevation={1}>
            {/* Release Header */}
            <div className="release-header">
              <div className="release-header-left">
                <div className="release-version">
                  <Icon icon={IconNames.TAG} size={20} />
                  <H3 style={{ margin: 0 }}>VERSION {release.version}</H3>
                </div>
                <div className="release-meta">
                  <Tag
                    round
                    style={{ backgroundColor: getStatusColor(release.status) }}
                  >
                    {release.status.toUpperCase()}
                  </Tag>
                  <Tag minimal>
                    <Icon icon={IconNames.CODE} size={12} style={{ marginRight: '4px' }} />
                    {release.codename}
                  </Tag>
                  <Tag minimal>
                    <Icon icon={IconNames.CALENDAR} size={12} style={{ marginRight: '4px' }} />
                    {release.releaseDate}
                  </Tag>
                </div>
              </div>
            </div>

            <Divider />

            {/* Major Features */}
            {release.majorFeatures && release.majorFeatures.length > 0 && (
              <div className="release-section">
                <H4 className="section-title">
                  <Icon icon={IconNames.STAR} size={18} />
                  MAJOR FEATURES
                </H4>
                <div className="features-grid">
                  {release.majorFeatures.map((feature: ChangelogFeature) => (
                    <Card key={feature.id} className="feature-card" elevation={0}>
                      <div className="feature-header">
                        <Tag
                          minimal
                          round
                          style={{ backgroundColor: getImpactColor(feature.impact) }}
                        >
                          {feature.impact.toUpperCase()}
                        </Tag>
                        <Tag minimal small>#{feature.id}</Tag>
                      </div>
                      <H5 className="feature-title">{feature.title}</H5>
                      <p className="feature-description">{feature.description}</p>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Component Upgrades */}
            {release.upgrades && release.upgrades.length > 0 && (
              <div className="release-section">
                <H4 className="section-title">
                  <Icon icon={IconNames.REFRESH} size={18} />
                  COMPONENT UPGRADES
                </H4>
                <div className="upgrades-table">
                  <table>
                    <thead>
                      <tr>
                        <th>COMPONENT</th>
                        <th>VERSION</th>
                        <th>DESCRIPTION</th>
                      </tr>
                    </thead>
                    <tbody>
                      {release.upgrades.map((upgrade, index) => (
                        <tr key={index}>
                          <td className="component-name">{upgrade.component}</td>
                          <td>
                            <Tag minimal small>{upgrade.version}</Tag>
                          </td>
                          <td className="description">{upgrade.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Improvements */}
            {release.improvements && release.improvements.length > 0 && (
              <div className="release-section">
                <H4 className="section-title">
                  <Icon icon={IconNames.UPGRADED} size={18} />
                  IMPROVEMENTS
                </H4>
                <ul className="change-list improvements-list">
                  {release.improvements.map((item, index) => (
                    <li key={index}>
                      <Icon icon={IconNames.TICK_CIRCLE} size={14} style={{ color: Colors.BLUE3 }} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Changes Grid */}
            {release.changes && (
              <div className="release-section">
                <H4 className="section-title">
                  <Icon icon={IconNames.CHANGES} size={18} />
                  DETAILED CHANGES
                </H4>
                <div className="changes-grid">
                  {/* Updated */}
                  {release.changes.updated && release.changes.updated.length > 0 && (
                    <div className="change-column">
                      <div className="change-column-header updated">
                        <Icon icon={IconNames.TICK_CIRCLE} size={16} />
                        UPDATED
                      </div>
                      <ul className="change-list">
                        {release.changes.updated.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Fixed */}
                  {release.changes.fixed && release.changes.fixed.length > 0 && (
                    <div className="change-column">
                      <div className="change-column-header fixed">
                        <Icon icon={IconNames.WARNING_SIGN} size={16} />
                        FIXED
                      </div>
                      <ul className="change-list">
                        {release.changes.fixed.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Removed */}
                  {release.changes.removed && release.changes.removed.length > 0 && (
                    <div className="change-column">
                      <div className="change-column-header removed">
                        <Icon icon={IconNames.CROSS_CIRCLE} size={16} />
                        REMOVED
                      </div>
                      <ul className="change-list">
                        {release.changes.removed.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Bug Fixes */}
            {release.bugFixes && release.bugFixes.length > 0 && (
              <div className="release-section">
                <H4 className="section-title">
                  <Icon icon={IconNames.BUG} size={18} />
                  BUG FIXES
                </H4>
                <ul className="change-list bugfixes-list">
                  {release.bugFixes.map((fix, index) => (
                    <li key={index}>
                      <Icon icon={IconNames.RESOLVE} size={14} style={{ color: Colors.GREEN3 }} />
                      <span>{typeof fix === 'string' ? fix : fix.description}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Release Notes */}
            {release.notes && (
              <div className="release-section release-notes">
                <Icon icon={IconNames.INFO_SIGN} size={16} style={{ marginRight: '8px' }} />
                <em>{release.notes}</em>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
});

export default ChangelogPage;

